import { normalizeExpenseType } from "../expenseType.js";
import { normalizeScopeDateContext } from "../scopeDate.js";
import {
  calculateMonthlyImpliedSpend,
  calculateProjectedAnnualSpend,
  filterExpenseRowsThroughToday
} from "./projectionMath.js";

function parseExpenseDate(dateText) {
  if (!dateText) {
    return null;
  }

  const parsed = new Date(`${dateText}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function buildBudgetCategoryKey(row) {
  if (row?.id) {
    return `id:${row.id}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

function buildExpenseCategoryKey(row) {
  if (row?.categoryId) {
    return `id:${row.categoryId}`;
  }

  return `name:${String(row?.category ?? "").trim()}`;
}

function groupExpenseRowsByCategory(expenseRows = [], type = null) {
  return expenseRows.reduce((groups, row) => {
    if (type && normalizeExpenseType(row.type) !== type) {
      return groups;
    }

    const key = buildExpenseCategoryKey(row);
    const current = groups.get(key) ?? [];
    current.push(row);
    groups.set(key, current);
    return groups;
  }, new Map());
}

function sumExpenseAmounts(expenseRows = []) {
  return expenseRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);
}

export function summarizeBudgetRows(rows, expenseRows = [], dateInput) {
  const dateContext = normalizeScopeDateContext(dateInput);
  const elapsedDays = dateContext.elapsedDays;
  const elapsedMonths = dateContext.elapsedMonths;
  const remainingDays = dateContext.remainingDays;
  const actualExpenseRows = filterExpenseRowsThroughToday(expenseRows, dateContext);
  const ongoingExpenseRowsByCategory = groupExpenseRowsByCategory(actualExpenseRows, "ongoing");
  const irregularExpenseRowsByCategory = groupExpenseRowsByCategory(actualExpenseRows, "irregular");
  const summary = rows.reduce((accumulator, row) => {
    const ongoingBudget = Number(row.ongoingBudget ?? 0);
    const irregularBudget = Number(row.irregularBudget ?? 0);
    const categoryKey = buildBudgetCategoryKey(row);
    const ongoingExpenseRows = expenseRows.filter((expenseRow) => (
      normalizeExpenseType(expenseRow.type) === "ongoing"
      && buildExpenseCategoryKey(expenseRow) === categoryKey
    ));
    const actualOngoingSpent = sumExpenseAmounts(ongoingExpenseRowsByCategory.get(categoryKey) ?? []);
    const actualIrregularSpent = sumExpenseAmounts(irregularExpenseRowsByCategory.get(categoryKey) ?? []);
    const ongoingImplied = calculateMonthlyImpliedSpend(ongoingExpenseRows, dateContext);
    const ongoingProjected = calculateProjectedAnnualSpend(ongoingExpenseRows, dateContext);
    const irregularProjected = Math.max(actualIrregularSpent, irregularBudget);

    accumulator.total.budget += (ongoingBudget * 12) + irregularBudget;
    accumulator.total.spent += actualOngoingSpent + actualIrregularSpent;
    accumulator.total.projected += ongoingProjected + irregularProjected;

    accumulator.ongoing.budget += ongoingBudget;
    accumulator.ongoing.spent += actualOngoingSpent;
    accumulator.ongoing.implied += ongoingImplied;

    accumulator.irregular.budget += irregularBudget;
    accumulator.irregular.spent += actualIrregularSpent;

    return accumulator;
  }, {
    total: {
      budget: 0,
      spent: 0,
      projected: 0
    },
    ongoing: {
      budget: 0,
      spent: 0,
      implied: 0
    },
    irregular: {
      budget: 0,
      spent: 0
    }
  });

  summary.elapsedDays = elapsedDays;
  summary.elapsedMonths = elapsedMonths;
  summary.remainingDays = remainingDays;
  summary.irregular.remaining = summary.irregular.budget - summary.irregular.spent;
  summary.ongoing.dailyBudget = summary.ongoing.budget / 30;
  summary.ongoing.monthlyAverage = summary.ongoing.spent / elapsedMonths;
  summary.total.monthlySpend = (summary.total.spent / elapsedDays) * 30;
  summary.total.remaining = summary.total.budget - summary.total.spent;
  summary.total.remainingMonthly = remainingDays > 0
    ? (summary.total.remaining / remainingDays) * 30
    : 0;

  return summary;
}

function isCurrentMonth(dateText, dateContext) {
  const parsedDate = parseExpenseDate(dateText);
  if (!parsedDate || !dateContext.isCurrentYear) {
    return false;
  }

  return parsedDate.getFullYear() === dateContext.scopedYear
    && parsedDate.getMonth() === dateContext.currentMonthIndex;
}

export function summarizeMonthlyExpenseRows(expenseRows, dateInput) {
  const dateContext = normalizeScopeDateContext(dateInput);
  return expenseRows.reduce((accumulator, row) => {
    if (!isCurrentMonth(row.date, dateContext)) {
      return accumulator;
    }

    const parsedDate = parseExpenseDate(row.date);
    if (!parsedDate || parsedDate.getDate() > dateContext.currentDate) {
      return accumulator;
    }

    const amount = Number(row.amount ?? 0);
    const type = normalizeExpenseType(row.type);

    accumulator.total.spentThisMonth += amount;

    if (type === "ongoing") {
      accumulator.ongoing.spentThisMonth += amount;
    }

    if (type === "irregular") {
      accumulator.irregular.spentThisMonth += amount;
    }

    return accumulator;
  }, {
    total: {
      spentThisMonth: 0
    },
    ongoing: {
      spentThisMonth: 0
    },
    irregular: {
      spentThisMonth: 0
    }
  });
}

export function budgetUsagePercent(usedAmount, budgetAmount) {
  if (budgetAmount <= 0) {
    return usedAmount > 0 ? 999 : 100;
  }

  return (usedAmount / budgetAmount) * 100;
}
